import { authenticatedFetch } from './apiUtils';
import { getApiUrl } from '../config';

const API_URL = getApiUrl();

export interface Budget {
  _id: string;
  id?: string;
  name: string;
  icon: string;
  amount: number;
  spent: number;
  budgetType?: 'Expense' | 'Savings' | 'Expense Fund';
  savingsGoal?: 'Target Goal' | 'Monthly Goal';
  targetDate?: string;
  expensePeriodValue?: number;
  expensePeriodUnit?: 'Months' | 'Years';
}

export interface Transaction {
  _id: string;
  budget: string;
  amount: number;
  datetime: string;
  note: string;
  transactionType?: 'expense' | 'savings';
  userId: string;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchBudgets(): Promise<Budget[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/budgets`, { headers });
  if (!res.ok) throw new Error('Failed to fetch budgets');
  return res.json();
}

export async function fetchAllBudgets(): Promise<Budget[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/budgets`, { headers });
  if (!res.ok) throw new Error('Failed to fetch budgets');
  return res.json();
}

export async function fetchBudgetsWithTransactions(year: number, month: number): Promise<Budget[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/budgets/with-transactions?year=${year}&month=${month}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch budgets with transactions');
  return res.json();
}

export async function fetchSavingsBudgets(year: number, month: number): Promise<Budget[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/budgets/savings?year=${year}&month=${month}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch savings budgets');
  return res.json();
}

export async function createBudget({ name, amount, icon, budgetType = 'Expense', savingsGoal, targetDate, expensePeriodValue, expensePeriodUnit }: { name: string; amount: number; icon: string; budgetType?: 'Expense' | 'Savings' | 'Expense Fund'; savingsGoal?: 'Target Goal' | 'Monthly Goal'; targetDate?: string; expensePeriodValue?: number; expensePeriodUnit?: 'Months' | 'Years' }): Promise<Budget> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/budgets`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name, amount, icon, category: 'General', budgetType, savingsGoal, targetDate, expensePeriodValue, expensePeriodUnit })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to create budget');
  return res.json();
}

export async function updateBudget(id: string, { name, amount, icon, budgetType, savingsGoal, targetDate, expensePeriodValue, expensePeriodUnit }: { name: string; amount: number; icon: string; budgetType?: 'Expense' | 'Savings' | 'Expense Fund'; savingsGoal?: 'Target Goal' | 'Monthly Goal'; targetDate?: string; expensePeriodValue?: number; expensePeriodUnit?: 'Months' | 'Years' }): Promise<Budget> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/budgets/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ name, amount, icon, category: 'General', budgetType, savingsGoal, targetDate, expensePeriodValue, expensePeriodUnit })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to update budget');
  return res.json();
}

export async function deleteBudget(id: string): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/budgets/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete budget');
}

export async function createTransaction({ 
  budget, 
  amount, 
  datetime, 
  note,
  transactionType = 'expense'
}: { 
  budget: string; 
  amount: number; 
  datetime: string; 
  note: string;
  transactionType?: 'expense' | 'savings';
}): Promise<Transaction> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/transactions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ budget, amount, datetime, note, transactionType })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to create transaction');
  return res.json();
}

export async function fetchTransactionsForBudget(
  budgetId: string, 
  year: number, 
  month: number
): Promise<Transaction[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(
    `${API_URL}/transactions?budgetId=${budgetId}&year=${year}&month=${month}`, 
    { headers }
  );
  if (!res.ok) throw new Error('Failed to fetch transactions');
  const data = await res.json();
  return data.transactions || [];
}

export async function fetchAllTransactionsForTimeframe(
  year: number, 
  month: number
): Promise<Transaction[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(
    `${API_URL}/transactions/all-for-timeframe?year=${year}&month=${month}`, 
    { headers }
  );
  if (!res.ok) throw new Error('Failed to fetch all transactions for timeframe');
  return res.json();
}

export async function updateTransaction(
  transactionId: string,
  { budget, amount, datetime, note, transactionType }: { 
    budget: string; 
    amount: number; 
    datetime: string; 
    note: string;
    transactionType?: 'expense' | 'savings';
  }
): Promise<Transaction> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/transactions/${transactionId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ budget, amount, datetime, note, transactionType })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to update transaction');
  return res.json();
}

export async function deleteTransaction(transactionId: string): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/transactions/${transactionId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete transaction');
}

export async function bulkCreateTransactions(transactions: {
  budget: string;
  amount: number;
  datetime: string;
  note: string;
}[]): Promise<{
  message: string;
  transactions: Transaction[];
  count: number;
}> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/transactions/bulk`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ transactions })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to bulk create transactions');
  return res.json();
}

export async function aiAutoReview(transactions: {
  id: string;
  amount: string;
  datetime: string;
  note: string;
  budget: string | null;
}[]): Promise<{
  message: string;
  transactions: {
    id: string;
    amount: string;
    datetime: string;
    note: string;
    budget: string;
  }[];
  count: number;
}> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/transactions/ai-review`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ transactions })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to auto-review transactions');
  return res.json();
}

export interface MappingProfile {
  _id: string;
  name: string;
  datetimeField: string;
  amountField: string;
  noteField: string;
  userId: string;
  createdAt?: string;
  updatedAt?: string;
}

export async function fetchMappingProfiles(): Promise<MappingProfile[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/mapping-profiles`, { headers });
  if (!res.ok) throw new Error('Failed to fetch mapping profiles');
  return res.json();
}

export async function createMappingProfile({
  name,
  datetimeField,
  amountField,
  noteField
}: {
  name: string;
  datetimeField: string;
  amountField: string;
  noteField: string;
}): Promise<MappingProfile> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/mapping-profiles`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name, datetimeField, amountField, noteField })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to create mapping profile');
  return res.json();
}

export async function updateMappingProfile(
  id: string,
  {
    name,
    datetimeField,
    amountField,
    noteField
  }: {
    name: string;
    datetimeField: string;
    amountField: string;
    noteField: string;
  }
): Promise<MappingProfile> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/mapping-profiles/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ name, datetimeField, amountField, noteField })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to update mapping profile');
  return res.json();
}

export interface ImportRule {
  _id: string;
  noteMatchType: 'Equals' | 'Includes';
  noteMatchValue: string;
  amountMatch: number | null;
  updatedNote: string;
  budget: string | Budget | null;
  skip?: boolean;
  userId: string;
  createdAt?: string;
  updatedAt?: string;
}

export async function fetchImportRules(): Promise<ImportRule[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/import-rules`, { headers });
  if (!res.ok) throw new Error('Failed to fetch import rules');
  return res.json();
}

export async function createImportRule({
  noteMatchType,
  noteMatchValue,
  amountMatch,
  updatedNote,
  budget,
  skip
}: {
  noteMatchType: 'Equals' | 'Includes';
  noteMatchValue: string;
  amountMatch?: number | null;
  updatedNote: string;
  budget?: string;
  skip?: boolean;
}): Promise<ImportRule> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/import-rules`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ noteMatchType, noteMatchValue, amountMatch, updatedNote, budget, skip })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to create import rule');
  return res.json();
}

export async function updateImportRule(
  id: string,
  {
    noteMatchType,
    noteMatchValue,
    amountMatch,
    updatedNote,
    budget,
    skip
  }: {
    noteMatchType: 'Equals' | 'Includes';
    noteMatchValue: string;
    amountMatch?: number | null;
    updatedNote: string;
    budget?: string;
    skip?: boolean;
  }
): Promise<ImportRule> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/import-rules/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ noteMatchType, noteMatchValue, amountMatch, updatedNote, budget, skip })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to update import rule');
  return res.json();
}

export async function deleteImportRule(id: string): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/import-rules/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to delete import rule');
}

// EOM Review API
export interface EOMCheckResponse {
  hasUnreviewedEOM: boolean;
  year?: number;
  month?: number;
}

export interface EOMBudgetData {
  id: string;
  name: string;
  icon: string;
  amount: number;
  spent: number;
  remaining: number;
  percentageRemaining: number;
  isOverBudget: boolean;
  overBudgetAmount: number;
  underutilizedAmount: number;
}

export interface EOMExpenseFundData {
  id: string;
  name: string;
  icon: string;
  amount: number;
  monthlyGoal: number;
  monthlySavings: number;
  leftToSave: number;
  percentageSaved: number;
  netAmount: number;
  isNegative: boolean;
}

export interface EOMSavingsData {
  id: string;
  name: string;
  icon: string;
  amount: number;
  monthlyGoal: number;
  monthlySavings: number;
  leftToSave: number;
  percentageSaved: number;
}

export interface EOMData {
  year: number;
  month: number;
  expenses: {
    totalBudget: number;
    totalSpent: number;
    totalRemaining: number;
    totalOverBudget: number;
    totalUnderutilized: number;
    percentageRemaining: number;
    budgets: EOMBudgetData[];
  };
  expenseFunds: {
    budgets: EOMExpenseFundData[];
    totalLeftToSave: number;
  };
  savings: {
    budgets: EOMSavingsData[];
    totalLeftToSave: number;
  };
  overallSavings: {
    totalGoal: number;
    totalSaved: number;
    totalRemaining: number;
    percentage: number;
  };
  suggestedSavings: number | null;
  overallNet: number;
}

export async function checkUnreviewedEOM(): Promise<EOMCheckResponse> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/eom/check`, { headers });
  if (!res.ok) throw new Error('Failed to check unreviewed EOM');
  return res.json();
}

export async function getEOMData(year: number, month: number): Promise<EOMData> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/eom/${year}/${month}`, { headers });
  if (!res.ok) throw new Error('Failed to fetch EOM data');
  return res.json();
}

export async function ignoreEOM(year: number, month: number): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/eom/${year}/${month}/ignore`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to ignore EOM');
}

export async function markAsSaved(budgetIds: string[], year: number, month: number): Promise<{ message: string; transactions: Transaction[] }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/eom/mark-saved`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ budgetIds, year, month })
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Failed to mark as saved');
  return res.json();
}

export interface EOMReview {
  _id: string;
  year: number;
  month: number;
  reviewed: boolean;
  reviewedAt: string | null;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export async function getAllEOMReviews(): Promise<EOMReview[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const res = await authenticatedFetch(`${API_URL}/eom/reviews`, { headers });
  if (!res.ok) throw new Error('Failed to fetch EOM reviews');
  return res.json();
} 